// src/components/LivePreview.jsx
import React, { useState, useRef, useEffect } from "react";

// Helper component to render input preview based on question type
function QuestionPreview({
  type,
  label,
  options = [],
  value,
  onChange,
  sliderMin,
  sliderMax,
  sliderStep,
  instructionStyle,
  instructionMediaUrl,
  instructionMediaName,
}) {
  switch (type) {
    case "short_text":
      return <input style={{ width: "80%", padding: 4 }} placeholder="Short answer..." disabled />;
    case "paragraph":
      return <textarea style={{ width: "80%", minHeight: 48, padding: 4 }} placeholder="Paragraph..." disabled />;
    case "multiple_choice": {
      const opts = Array.isArray(options) && options.length ? options : ["Option 1", "Option 2"];
      const selected = value ?? null;
      return (
        <div className="preview-options">
          {opts.map((o, i) => {
            const isSel = selected === o;
            return (
              <button
                key={i}
                type="button"
                className={`preview-option ${isSel ? "selected" : ""}`}
                onClick={() => onChange(isSel ? null : o)}
                title={o}
              >
                {o}
              </button>
            );
          })}
        </div>
      );
    }
    case "checkbox":
      return (
        <div
          className="preview-checkbox-row"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            minHeight: 30,
            paddingTop: 2
          }}
        >
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span style={{ fontSize: 15 }}>{label || "Checkbox"}</span>
        </div>
      );
    case "date":
      return (
        <input
          type="date"
          className="preview-input"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "signature":
      return (
        <SignaturePreview
          value={value}
          onChange={onChange}
        />
      );
    case "slider": {
      const min = typeof sliderMin === "number" ? sliderMin : 0;
      const max = typeof sliderMax === "number" ? sliderMax : 10;
      const step =
        typeof sliderStep === "number" && sliderStep > 0 ? sliderStep : 1;
      let current = typeof value === "number" ? value : min;
      if (current < min) current = min;
      if (current > max) current = max;

      return (
        <div style={{ width: "100%" }}>
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            className="preview-range"
            style={{ width: "100%" }}
            value={current}
            onChange={(e) => onChange(Number(e.target.value))}
          />
          <div
            className="slider-preview-scale"
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 4,
              fontSize: 11,
              color: "#6b7280",
            }}
          >
            <span>{min}</span>
            <span style={{ fontWeight: 600, color: "#111827" }}>
              {current}
            </span>
            <span>{max}</span>
          </div>
        </div>
      );
    }
    case "media":
      return (
        <div
          style={{
            border: "1px dashed #9ca3af",
            borderRadius: 8,
            padding: 10,
            fontSize: 12,
            color: "#6b7280",
            background: "#f9fafb",
          }}
        >
          📎 Media upload (images / videos)
        </div>
      );
    case "site":
    case "person":
    case "location":
      return <input style={{ width: "80%", padding: 4 }} placeholder={`Pick ${type}`} disabled />;
    case "annotation":
      return (
        <div style={{ width: "100%", margin: "6px 0" }}>
          <div
            style={{
              border: "1px dashed #d1d5db",
              borderRadius: 8,
              padding: 8,
              background: "#f9fafb",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: "999px",
                border: "1px solid #d1d5db",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
                flexShrink: 0,
              }}
            >
              ✏️
            </div>
            <div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#111827",
                }}
              >
                Annotation
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "#6b7280",
                }}
              >
                Upload an image and draw on it
              </div>
            </div>
          </div>
        </div>
      );
    case "document-number":
      return <input style={{ width: "60%", padding: 4 }} placeholder="Document #" disabled />;
    case "instruction": {
      const styleKey = (instructionStyle || "info").toLowerCase();
      let fg = "#1976d2";
      let bg = "#e7f0fd";

      if (styleKey === "danger") {
        fg = "#b91c1c";
        bg = "#fee2e2";
      } else if (styleKey === "caution") {
        fg = "#92400e";
        bg = "#fef3c7";
      } else if (styleKey === "positive") {
        fg = "#166534";
        bg = "#dcfce7";
      }

      const url = instructionMediaUrl || "";
      const isPdf = typeof url === "string" && url.toLowerCase().includes(".pdf");

      return (
        <div
          style={{
            color: fg,
            background: bg,
            padding: 8,
            borderRadius: 8,
            fontSize: 14,
          }}
        >
          {label && (
            <div
              style={{
                fontStyle: "italic",
                marginBottom: instructionMediaUrl ? 6 : 0,
              }}
            >
              {label}
            </div>
          )}

          {instructionMediaUrl && (
            <div style={{ marginTop: 4 }}>
              {isPdf ? (
                <iframe
                  src={instructionMediaUrl}
                  title={instructionMediaName || "Instruction attachment"}
                  style={{
                    width: "100%",
                    height: 360,
                    border: "none",
                    borderRadius: 6,
                    background: "#fff",
                  }}
                />
              ) : (
                <img
                  src={instructionMediaUrl}
                  alt={instructionMediaName || "Instruction attachment"}
                  style={{
                    width: "100%",
                    maxHeight: 400,
                    borderRadius: 6,
                    objectFit: "contain",
                    display: "block",
                    background: "#fff",
                  }}
                />
              )}
            </div>
          )}
        </div>
      );
    }
    default:
      return <span style={{ color: "#bbb" }}>{type}</span>;
  }
}

function SignaturePreview({ value, onChange }) {
  const [isPadOpen, setIsPadOpen] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const padCanvasRef = useRef(null);
  const drawingRef = useRef(false);

  // ensure we always have all fields including the image
  function getSafeValue(v = value) {
    if (v && typeof v === "object") {
      return {
        fullName: "",
        hasSignature: false,
        signedAt: null,
        signatureData: null,
        ...v,
      };
    }
    if (typeof v === "string") {
      return {
        fullName: v,
        hasSignature: false,
        signedAt: null,
        signatureData: null,
      };
    }
    return {
      fullName: "",
      hasSignature: false,
      signedAt: null,
      signatureData: null,
    };
  }

  const safeValue = getSafeValue();

  const handleNameChange = (e) => {
    const current = getSafeValue();
    onChange({ ...current, fullName: e.target.value });
  };

  const resetCanvas = () => {
    const canvas = padCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    ctx.fillStyle = "#f3f4f6";
    ctx.fillRect(0, 0, rect.width, rect.height);

    ctx.strokeStyle = "#d1d5db";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 4]);
    const midY = rect.height * 0.65;
    ctx.beginPath();
    ctx.moveTo(16, midY);
    ctx.lineTo(rect.width - 16, midY);
    ctx.stroke();
    ctx.setLineDash([]);
  };

  useEffect(() => {
    if (isPadOpen) {
      setTimeout(resetCanvas, 0);
    }
  }, [isPadOpen]);

  const openPad = () => {
    setIsPadOpen(true);
    setHasDrawn(false);
  };

  const handleClear = () => {
    resetCanvas();
    setHasDrawn(false);
  };

  const getPos = (e) => {
    const canvas = padCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return {
      canvas,
      x: point.clientX - rect.left,
      y: point.clientY - rect.top,
    };
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    const pos = getPos(e);
    if (!pos) return;
    const ctx = pos.canvas.getContext("2d");
    drawingRef.current = true;
    ctx.strokeStyle = "#111827";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const handlePointerMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const pos = getPos(e);
    if (!pos) return;
    const ctx = pos.canvas.getContext("2d");
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const handlePointerUp = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    drawingRef.current = false;
  };

  const handleDone = () => {
    const current = getSafeValue();
    const hasSignature = hasDrawn || current.hasSignature;

    // capture image if we actually drew something
    let signatureData = current.signatureData || null;
    const canvas = padCanvasRef.current;
    if (hasDrawn && canvas) {
      try {
        signatureData = canvas.toDataURL("image/png");
      } catch {
        // ignore preview errors
      }
    }

    const signedAt = hasDrawn ? Date.now() : current.signedAt || null;

    onChange({ ...current, hasSignature, signedAt, signatureData });
    setIsPadOpen(false);
    setHasDrawn(false);
  };

  const signedAtLabel =
    safeValue.signedAt != null
      ? new Date(safeValue.signedAt).toLocaleString()
      : null;

  return (
    <div style={{ margin: "4px 0" }}>
      {/* name + icon row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 6,
        }}
      >
        <input
          style={{
            flex: 1,
            padding: "8px 10px",
            borderRadius: 10,
            border: "1px solid #d1d5db",
            fontSize: 13,
          }}
          placeholder="Full name"
          value={safeValue.fullName || ""}
          onChange={handleNameChange}
        />
        <button
          type="button"
          onClick={openPad}
          style={{
            width: 35,
            height: 35,
            borderRadius: 10,
            border: "none",
            background: "#6366f1",
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            boxShadow: "0 6px 12px rgba(99,102,241,0.35)",
            cursor: "pointer",
          }}
          title={safeValue.hasSignature ? "View / re-sign" : "Sign"}
        >
          ✍️
        </button>
      </div>

      {/* Signature box – only AFTER signature exists */}
      {safeValue.hasSignature && (
        <div
          style={{
            borderRadius: 12,
            border: "1px dashed #d1d5db",
            padding: 10,
            background: "#f3f4f6",
          }}
        >
          <div
            style={{
              height: 70,
              borderRadius: 10,
              background: "#f9fafb",
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {/* dotted line */}
            <div
              style={{
                position: "absolute",
                left: 20,
                right: 20,
                bottom: 18,
                borderBottom: "1px dashed #d1d5db",
              }}
            />
            {/* signature image or placeholder text */}
            {safeValue.signatureData ? (
              <img
                src={safeValue.signatureData}
                alt="Signature preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  zIndex: 1,
                }}
              />
            ) : (
              <div
                style={{
                  fontSize: 11,
                  color: "#9ca3af",
                  zIndex: 1,
                }}
              >
                Sign above the dotted line
              </div>
            )}
          </div>
          {signedAtLabel && (
            <div
              style={{
                marginTop: 4,
                fontSize: 11,
                color: "#6b7280",
              }}
            >
              Signed: {signedAtLabel}
            </div>
          )}
        </div>
      )}

      {/* SMALL CENTERED sign pad */}
      {isPadOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 60,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: "min(480px, 90%)",
              height: 260,
              borderRadius: 20,
              background: "#f9fafb",
              boxShadow: "0 18px 40px rgba(15,23,42,0.35)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* top bar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 16px",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <button
                type="button"
                onClick={handleClear}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#2563eb",
                  fontSize: 14,
                }}
              >
                Clear
              </button>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                {new Date().toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
              <button
                type="button"
                onClick={handleDone}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#2563eb",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Done
              </button>
            </div>

            {/* canvas area */}
            <div
              style={{
                flex: 1,
                padding: "12px 16px 16px",
                background: "#e5e7eb",
                borderBottomLeftRadius: 20,
                borderBottomRightRadius: 20,
              }}
            >
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  borderRadius: 16,
                  background: "#f3f4f6",
                  padding: "8px 10px",
                }}
              >
                <canvas
                  ref={padCanvasRef}
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 14,
                    touchAction: "none",
                    background: "transparent",
                  }}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerLeave={handlePointerUp}
                />
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 22,
                    textAlign: "center",
                    pointerEvents: "none",
                  }}
                >
                  <div
                    style={{
                      borderBottom: "1px dashed #9ca3af",
                      margin: "0 32px",
                    }}
                  />
                  <div
                    style={{
                      marginTop: 8,
                      fontSize: 13,
                      color: "#9ca3af",
                      fontWeight: 500,
                    }}
                  >
                    Sign above the dotted line
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function LivePreview({ sections, className = "" }) {
  const pageCount = Array.isArray(sections) ? sections.length : 0;
  const [page, setPage] = useState(0);
  const clamp = (n, lo, hi) => Math.max(lo, Math.min(n, hi));

  const current = sections?.[page] || { title: "Untitled Page", questions: [] };

  // local, throwaway preview answers (does not save to store)
  const [answers, setAnswers] = useState({});

  // ============================================================
  // LIVE PREVIEW: FLATTEN nested section questions (sectionItems)
  // ============================================================
  const flattenQuestions = (items = []) => {
    const out = [];

    items.forEach((q) => {
      if (!q || !q.type) return;

      const lowerType = q.type.toLowerCase();

      if (lowerType === "section") {
        // Render a section header row
        out.push(q);

        // Recursively flatten its children
        if (Array.isArray(q.sectionItems)) {
          out.push(...flattenQuestions(q.sectionItems));
        }
      } else {
        // Regular question
        out.push(q);
      }
    });

    return out;
  };

  // Flatten nested question/sectionItems for preview
  const flatQuestions = flattenQuestions(current.questions || []);

  // per-page completion based on how many QUESTIONS (non-section / non-instruction)
  // on THIS page have a non-empty answer
  const pageQuestions = flatQuestions.filter((q) => {
    const t = (q.type || "").toLowerCase();
    return t !== "instruction" && t !== "section";
  });

  const totalQs = pageQuestions.length;

  const answered = pageQuestions.reduce((sum, q) => {
    const v = answers[q.id];

    // nothing stored at all
    if (v === undefined || v === null) return sum;

    // empty string
    if (typeof v === "string" && v.trim() === "") return sum;

    // empty array (e.g. multi-select)
    if (Array.isArray(v) && v.length === 0) return sum;

    // for checkbox, any boolean means the user has interacted => count as answered
    const t = (q.type || "").toLowerCase();
    if (t === "checkbox" && typeof v === "boolean") {
      return sum + 1;
    }

    // for anything else (numbers, objects, non-empty strings/arrays), treat as answered
    return sum + 1;
  }, 0);

  const pct = totalQs ? Math.round((answered / totalQs) * 100) : 0;

  const prev = () => setPage((p) => clamp(p - 1, 0, pageCount - 1));
  const next = () => setPage((p) => clamp(p + 1, 0, pageCount - 1));

  return (
    <aside className={`preview-pane ${className}`}>
      <div className="iphone-mockup">
        {/* SafetyCulture-like header */}
        <div className="preview-header">
          <div className="preview-header-text">
            Page <b>{Math.min(page + 1, pageCount || 1)}</b> of <b>{pageCount || 1}</b>
          </div>
          <div className="preview-header-text" style={{ textAlign: "right" }}>
            <span style={{ fontSize: 12, color: "#6b7280" }}>Completion</span>{" "}
            <b>{pct}%</b>
          </div>
        </div>

        {/* per-page progress bar instead of score */}
        <div
          style={{
            marginTop: 6,
            marginBottom: 10,
            width: "100%",
            height: 6,
            borderRadius: 999,
            background: "#e5e7eb",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: "100%",
              borderRadius: 999,
              background: "#4f46e5",
              transition: "width 0.2s ease-out",
            }}
          />
        </div>

        <div className="question-preview-list">
          <div key={current.id} style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 10 }}>{current.title || "Untitled Page"}</div>

            {flatQuestions.map((q, idx) => {
              const rawType = (q.type || "").toLowerCase();
              const isCheckbox = rawType === "checkbox";
              const isInstruction = rawType === "instruction";
              const isSectionRow = rawType === "section";

              if (isSectionRow) {
                return (
                  <div
                    key={q.id}
                    className="preview-section-row"
                    style={{
                      margin: "10px 0 6px",
                      padding: "6px 10px",
                      borderRadius: 8,
                      background: "#e0ecff",
                      color: "#1d4ed8",
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {q.label || `Section ${idx + 1}`}
                  </div>
                );
              }

              return (
                <div key={q.id} className="preview-question">
                  <div className="preview-question-card">
                    {!isCheckbox && !isInstruction && (
                      <div className="preview-question-title">
                        {q.label || `Question ${idx + 1}`}
                        {q.required && <span className="preview-required">*</span>}
                      </div>
                    )}

                    <QuestionPreview
                      type={q.type}
                      label={q.label}
                      options={
                        window.__builderSetsResolver
                          ? window.__builderSetsResolver(q)
                          : q.options
                      }
                      value={answers[q.id]}
                      onChange={(val) => setAnswers((a) => ({ ...a, [q.id]: val }))}
                      sliderMin={q.sliderMin}
                      sliderMax={q.sliderMax}
                      sliderStep={q.sliderStep}
                      instructionStyle={q.instructionStyle}
                      instructionMediaUrl={q.instructionMediaUrl}
                      instructionMediaName={q.instructionMediaName}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Pager */}
        <div className="preview-pager">
          <button
            onClick={prev}
            disabled={page <= 0}
            className={`preview-pager-btn ${page <= 0 ? "disabled" : ""}`}
          >
            ‹ Previous Page
          </button>
          <button
            onClick={next}
            disabled={page >= pageCount - 1}
            className={`preview-pager-btn ${page >= pageCount - 1 ? "disabled" : ""}`}
          >
            Next Page ›
          </button>
        </div>
      </div>
    </aside>
  );
}
